import { useState } from 'react';
import kit from './kit.module.css';
import styles from './DecisionExplanationDemoAgentic.module.css';

type Venue = 'harbor' | 'millrow';
type Section = 'venue' | 'catering';
type Conflict = 'open' | 'raise' | 'drop';

interface LogEntry {
  time: string;
  text: string;
  tag?: string;
}

const BUDGET = 4000;
const CATERING = 1750;
const VENUES: Record<Venue, { name: string; cost: number; note: string }> = {
  harbor: { name: 'Harbor Loft', cost: 2560, note: 'Step-free, 15 min walk from the station' },
  millrow: { name: 'Millrow Hall', cost: 3200, note: 'Step-free, next to the station' },
};

const fmt = (n: number) => `$${n.toLocaleString('en-US')}`;

const clock = (mins: number) => `${10 + Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;

const DEFAULT_FEEDBACK =
  'Reasoning stays collapsed until you want it. Open "Reasoning" on the venue to see why the agent chose it.';

export function DecisionExplanationDemoAgentic() {
  const [venue, setVenue] = useState<Venue>('harbor');
  const [open, setOpen] = useState<Section | null>(null);
  const [conflict, setConflict] = useState<Conflict>('open');
  const [log, setLog] = useState<LogEntry[]>([
    { time: '10:38', text: 'Searched 9 venues for 14 people in May' },
    { time: '10:40', text: 'Scored 4 step-free venues, reasoning saved', tag: 'Saved' },
    { time: '10:42', text: 'Plan ready - 1 conflict needs your call' },
  ]);
  const [feedback, setFeedback] = useState(DEFAULT_FEEDBACK);

  const v = VENUES[venue];
  const other = VENUES[venue === 'harbor' ? 'millrow' : 'harbor'];
  const catering = conflict === 'drop' ? 0 : CATERING;
  const total = v.cost + catering;
  const budget = conflict === 'raise' ? Math.max(BUDGET, v.cost + CATERING) : BUDGET;
  const over = v.cost + CATERING - BUDGET;

  const addLog = (text: string) =>
    setLog((l) => [...l, { time: clock(44 + l.length - 3), text, tag: 'You' }]);

  const toggle = (s: Section) => {
    const next = open === s ? null : s;
    setOpen(next);
    if (next) {
      setFeedback('Factors, trade-off and the option that lost - pulled from scoring saved at 10:40. Keep it or change it.');
    }
  };

  const changeVenue = () => {
    const next: Venue = venue === 'harbor' ? 'millrow' : 'harbor';
    setVenue(next);
    setOpen(null);
    addLog(`Venue changed to ${VENUES[next].name}`);
    setFeedback(
      next === 'millrow'
        ? `Switched to Millrow Hall. The total went up by ${fmt(640)}, so the budget gap grew too.`
        : 'Back to Harbor Loft. The plan and the budget gap updated.'
    );
  };

  const keep = () => {
    setOpen(null);
    setFeedback(`Kept ${v.name}. The explanation closed and the plan is unchanged.`);
  };

  const resolve = (choice: 'raise' | 'drop') => {
    setConflict(choice);
    if (choice === 'raise') {
      addLog(`Budget raised to ${fmt(v.cost + CATERING)}`);
      setFeedback('You chose catering over the original budget. The agent asked instead of deciding for you.');
    } else {
      addLog('Catering dropped to stay within budget');
      setFeedback('You chose the budget over catering. Nothing was dropped without your say.');
    }
  };

  const venueReasoning =
    venue === 'harbor'
      ? `Harbor Loft and Millrow Hall are both step-free. Harbor Loft is ${fmt(640)} cheaper; trade-off: 15 minutes further from the station. Millrow lost on price.`
      : `You picked Millrow Hall over the agent's choice. It is ${fmt(640)} more than Harbor Loft, which lost only because you preferred being next to the station.`;

  return (
    <div className={kit.root}>
      <div className={kit.message}>Plan a team offsite for 14 people in May. Must be step-free. Budget $4,000.</div>

      <div className={kit.log} aria-label="Agent activity">
        {log.map((e, i) => (
          <div className={kit.logRow} key={i}>
            <span className={kit.logTime}>{e.time}</span>
            <span className={`${kit.logDot} ${kit.logDotDone}`} aria-hidden="true" />
            <span>{e.text}</span>
            {e.tag ? <span className={kit.chip}>{e.tag}</span> : <span />}
          </div>
        ))}
      </div>

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>Output · Offsite plan</span>
          <span>Draft</span>
        </div>
        <h3 className={kit.proposalTitle}>Team offsite, May · 14 people</h3>

        <div className={styles.item}>
          <div className={styles.itemHead}>
            <div>
              <span className={kit.label}>Venue</span>
              <div className={styles.itemValue}>
                {v.name} · {fmt(v.cost)}
              </div>
              <div className={styles.itemNote}>{v.note}</div>
            </div>
            <button
              type="button"
              className={styles.disclosure}
              aria-expanded={open === 'venue'}
              onClick={() => toggle('venue')}
            >
              {open === 'venue' ? 'Hide reasoning' : 'Reasoning'}
            </button>
          </div>
          {open === 'venue' && (
            <div className={styles.reasoning}>
              <span className={kit.label}>Scored 10:40 · 4 step-free options</span>
              <p>{venueReasoning}</p>
              <div className={kit.row}>
                <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={changeVenue}>
                  Change to {other.name}
                </button>
                <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={keep}>
                  Keep it
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={styles.item}>
          <div className={styles.itemHead}>
            <div>
              <span className={kit.label}>Catering</span>
              <div className={styles.itemValue}>
                {conflict === 'drop' ? 'None - lunch at nearby cafes' : `Greenleaf Kitchen · ${fmt(CATERING)}`}
              </div>
            </div>
            <button
              type="button"
              className={styles.disclosure}
              aria-expanded={open === 'catering'}
              onClick={() => toggle('catering')}
            >
              {open === 'catering' ? 'Hide reasoning' : 'Reasoning'}
            </button>
          </div>
          {open === 'catering' && (
            <div className={styles.reasoning}>
              <span className={kit.label}>Scored 10:41 · 3 caterers</span>
              <p>
                {conflict === 'drop'
                  ? 'You dropped catering to stay within budget. Greenleaf Kitchen is still available if that changes.'
                  : 'Greenleaf Kitchen is the only caterer that delivers to the venue on a weekday. Trade-off: it pushes the plan over budget. Cheaper Basil & Co lost - no weekday delivery.'}
              </p>
              <div className={kit.row}>
                <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={keep}>
                  Keep it
                </button>
              </div>
            </div>
          )}
        </div>

        <dl className={`${kit.fields} ${styles.total}`}>
          <dt>Total</dt>
          <dd>
            {fmt(total)} of {fmt(budget)} budget
          </dd>
        </dl>
      </div>

      {conflict === 'open' ? (
        <div className={styles.conflict} role="group" aria-label="Conflict">
          <span className={`${kit.chip} ${kit.chipWarn}`}>Conflict</span>
          <p>
            Step-free venues with catering exceed $4,000 by {fmt(over)}. Which matters more?
          </p>
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => resolve('raise')}>
              Raise budget
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => resolve('drop')}>
              Drop catering
            </button>
          </div>
        </div>
      ) : (
        <div className={kit.row}>
          <span className={`${kit.chip} ${kit.chipGood}`}>Step-free kept</span>
          <span className={kit.chip}>{conflict === 'raise' ? 'Budget raised' : 'Catering dropped'}</span>
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
